import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions, ROLE_LABELS } from '../hooks/usePermissions';
import { RoleSwitcher, ImpersonationBanner } from './RoleSwitcher';
import NotificationCenter from './NotificationCenter';
import TrialBanner from './TrialBanner';
import BreakGlassBanner from './BreakGlassBanner';
import { Breadcrumbs } from './Breadcrumbs';
import { CommandPalette } from './CommandPalette';
import { BrandMark } from './BrandMark';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { 
  LayoutDashboard, 
  Users, 
  CalendarDays, 
  FileText,
  PoundSterling, 
  Settings, 
  LogOut,
  Menu,
  X,
  ClipboardList,
  UserCircle,
  BarChart3,
  Shield,
  Search,
  Briefcase,
  FileStack,
  Send,
  MapPin,
  HelpCircle
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isImpersonating, effectiveRole } = useAuth();
  const { 
    isMedic,
    role,  // This is the effective role from usePermissions
  } = usePermissions();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation items with role-based visibility
  // Ops Coordinator: Dashboard, Schedule, Medics, Shifts, Shift Requests, Compliance (limited), Contracts, Job Types, Expenses
  // Manager: + Reports, Audit Logs, full Compliance (same as Team Leader)
  // Admin: + Settings
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/schedule', label: 'Schedule', icon: CalendarDays, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/live-map', label: 'Live Map', icon: MapPin, roles: ['ops_coordinator', 'team_leader', 'manager', 'admin'] },
    { path: '/medics', label: 'Medics', icon: Users, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/shifts', label: 'Shifts', icon: ClipboardList, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/shift-requests', label: 'Shift Requests', icon: Send, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/leave', label: 'Leave', icon: CalendarDays, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/compliance', label: 'Compliance', icon: Shield, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/contracts', label: 'Contracts', icon: FileStack, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/job-types', label: 'Job Types', icon: Briefcase, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/expenses', label: 'Expenses', icon: PoundSterling, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
    { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['team_leader', 'manager', 'admin'] },
    { path: '/audit-logs', label: 'Audit Logs', icon: FileText, roles: ['team_leader', 'manager', 'admin'] },
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin'] },
    { path: '/help', label: 'Help & Support', icon: HelpCircle, roles: ['ops_coordinator', 'standard', 'team_leader', 'manager', 'admin'] },
  ];

  const medicNavItems = [
    { path: '/portal', label: 'My Dashboard', icon: LayoutDashboard },
    { path: '/portal/shifts', label: 'My Shifts', icon: CalendarDays },
    { path: '/portal/open-shifts', label: 'Open Shifts', icon: ClipboardList },
    { path: '/portal/availability', label: 'Availability', icon: Users },
    { path: '/portal/leave', label: 'My Leave', icon: CalendarDays },
    { path: '/portal/expenses', label: 'My Expenses', icon: PoundSterling },
    { path: '/portal/documents', label: 'Documents', icon: FileText },
    { path: '/portal/profile', label: 'Profile', icon: UserCircle },
    { path: '/help', label: 'Help & Support', icon: HelpCircle },
  ];

  // Use effective role for navigation filtering (respects impersonation)
  const currentNavItems = isMedic ? medicNavItems : navItems;
  const filteredItems = isMedic 
    ? currentNavItems 
    : currentNavItems.filter(item => item.roles.includes(role));

  // Get display-friendly role name - show effective role if impersonating or session role selected
  const { sessionRole } = useAuth();
  const displayRole = isImpersonating && effectiveRole 
    ? ROLE_LABELS[effectiveRole] || effectiveRole
    : sessionRole 
      ? ROLE_LABELS[sessionRole] || sessionRole
      : ROLE_LABELS[user?.role] || user?.role?.replace('_', ' ');

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50
        w-64 bg-[#1A1A1A] border-r border-[#3D3D3D]
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-[#3D3D3D]">
            <Link to={isMedic ? '/portal' : '/dashboard'} className="flex items-center gap-2" data-testid="sidebar-brand-link">
              <BrandMark variant="sidebar" />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-[#A1A1AA] hover:text-[#F2F2F2]"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {filteredItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                      transition-colors duration-200
                      ${isActive 
                        ? 'bg-[#1ABC9C]/10 text-[#1ABC9C]' 
                        : 'text-[#C7C7C7] hover:text-[#F2F2F2] hover:bg-white/5'
                      }
                    `}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t border-[#3D3D3D]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#1ABC9C]/20 flex items-center justify-center">
                <UserCircle className="w-6 h-6 text-[#1ABC9C]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#F2F2F2] truncate">{user?.name}</p>
                <p className="text-xs text-[#C7C7C7]">{displayRole}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-[#C7C7C7] hover:text-[#F2F2F2] hover:bg-white/5"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { user } = useAuth();
  const { isActualAdmin } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  
  const getPageTitle = () => {
    const path = location.pathname;
    const titles = {
      '/dashboard': 'Dashboard',
      '/schedule': 'Schedule',
      '/live-map': 'Live Tracking Map',
      '/medics': 'Medic Management',
      '/shifts': 'Shift Management',
      '/compliance': 'Compliance Management',
      '/expenses': 'Expense Management',
      '/reports': 'Reports',
      '/audit-logs': 'Audit Logs',
      '/settings': 'Settings',
      '/contracts': 'Contracts',
      '/job-types': 'Job Types',
      '/shift-requests': 'Shift Requests',
      '/help': 'Help & Support',
      '/portal': 'Medic Portal',
      '/portal/shifts': 'My Shifts',
      '/portal/open-shifts': 'Open Shifts',
      '/portal/availability': 'Availability',
      '/portal/expenses': 'My Expenses',
      '/portal/documents': 'Documents',
      '/portal/profile': 'Profile',
    };
    return titles[path] || 'Dashboard';
  };

  return (
    <header className="h-16 bg-[#1A1A1A] border-b border-[#3D3D3D] flex items-center justify-between gap-2 px-4 md:px-6">
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden text-[#C7C7C7] hover:text-[#F2F2F2] shrink-0"
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5" />
        </Button>
        <h1 className="text-base md:text-lg font-semibold text-[#F2F2F2] truncate">
          {getPageTitle()}
        </h1>
      </div>
      <div className="flex items-center gap-2 md:gap-4 text-sm text-[#C7C7C7] shrink-0">
        {/* iter72.7 — ⌘K / Ctrl-K global search hint. Clicking the pill
            dispatches the same keyboard event so touch users can open
            the palette too. */}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('elara:open-command-palette'))}
          className="hidden md:inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-white/10 bg-[#2A2A2A] text-xs text-[#A1A1AA] hover:text-white hover:border-white/20 transition-colors"
          data-testid="open-command-palette-btn"
          aria-label="Open search"
        >
          <Search className="w-3.5 h-3.5" /> Search
          <kbd className="ml-2 text-[10px] font-mono bg-[#1A1A1A] border border-white/10 rounded px-1.5 py-0.5">⌘K</kbd>
        </button>
        {user?.is_platform_admin && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/platform')}
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hidden sm:flex"
            data-testid="open-platform-console-btn"
          >
            <Shield className="w-4 h-4 mr-1" /> Platform
          </Button>
        )}
        <NotificationCenter />
        {isActualAdmin && <RoleSwitcher />}
        <div className="hidden sm:flex items-center gap-2 max-w-[200px]">
          <span className="hidden md:inline">Welcome,</span>
          <span className="text-[#F2F2F2] font-medium truncate">{user?.name}</span>
        </div>
      </div>
    </header>
  );
};

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-[#1A1A1A]">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <BreakGlassBanner />
        <TrialBanner />
        <ImpersonationBanner />
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Breadcrumbs />
        <CommandPalette />
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
